import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ConsentStatus } from '../../hooks/useCookieConsent';
import styles from './CookieConsent.module.css';

interface CookieConsentProps {
  status: ConsentStatus;
  onAccept: () => void;
  onReject: () => void;
}

/* ── Station data ── */
const useStations = (isIt: boolean) => [
  {
    id: 'what',
    label: isIt ? 'Cosa sono?' : 'What are they?',
    detail: isIt
      ? 'I cookie sono piccoli file di testo memorizzati dal browser per ricordare le tue preferenze e raccogliere dati statistici anonimi.'
      : 'Cookies are small text files stored by your browser to remember preferences and collect anonymous statistics.',
  },
  {
    id: 'why',
    label: isIt ? 'Perché?' : 'Why?',
    detail: isIt
      ? 'Utilizziamo Google Analytics per capire come viene usato il sito e migliorare la navigazione. I dati sono anonimi e aggregati.'
      : 'We use Google Analytics to understand site usage and improve navigation. Data is anonymous and aggregated.',
  },
  {
    id: 'data',
    label: isIt ? 'Sicurezza' : 'Security',
    detail: isIt
      ? 'Non raccogliamo dati personali identificabili. L\'indirizzo IP è anonimizzato. Nessuna condivisione con terze parti per pubblicità.'
      : 'We collect no personally identifiable data. IP addresses are anonymized. No data shared with third parties for advertising.',
  },
  {
    id: 'choices',
    label: isIt ? 'Le tue scelte' : 'Your choices',
    detail: isIt
      ? 'Puoi accettare o rifiutare in qualsiasi momento. Se rifiuti, nessun cookie analitico verrà installato. Puoi sempre cambiare idea dalla Cookie Policy.'
      : 'Accept or reject at any time. If you reject, no analytics cookies are installed. You can always change your mind on the Cookie Policy page.',
  },
];

/* ── Station positions along the SVG track ── */
const STATION_POSITIONS = [
  { cx: 60, cy: 30 },
  { cx: 180, cy: 30 },
  { cx: 300, cy: 30 },
  { cx: 420, cy: 30 },
];

const LEGAL_PATHS = ['/cookies', '/privacy'];

export const CookieConsent = ({ status, onAccept, onReject }: CookieConsentProps) => {
  const { i18n } = useTranslation();
  const isIt = i18n.language === 'it';
  const location = useLocation();
  const stations = useStations(isIt);

  const [animatingOut, setAnimatingOut] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [activeStation, setActiveStation] = useState<string | null>(null);

  const isLegalPage = LEGAL_PATHS.includes(location.pathname);
  const isVisible = status === 'pending' || animatingOut;

  // Lock body scroll only when full modal is visible (not on legal pages)
  useEffect(() => {
    if (isVisible && !isLegalPage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isVisible, isLegalPage]);

  if (!isVisible) return null;

  const handleAccept = () => {
    setAccepted(true);
    setAnimatingOut(true);
    setTimeout(() => {
      onAccept();
      setAnimatingOut(false);
    }, 800);
  };

  const handleReject = () => {
    setAnimatingOut(true);
    setTimeout(() => {
      onReject();
      setAnimatingOut(false);
    }, 400);
  };

  /* ───────── MINI BANNER for legal pages ───────── */
  if (isLegalPage) {
    return (
      <div className={`${styles.miniBanner} ${animatingOut ? styles.miniBannerExit : styles.miniBannerEnter}`}>
        <div className={styles.miniTrack}>
          <div className={styles.miniTrackDashes} />
        </div>
        <div className={styles.miniContent}>
          <p className={styles.miniText}>
            {isIt
              ? 'Questo sito utilizza cookie analitici (Google Analytics).'
              : 'This site uses analytics cookies (Google Analytics).'}
          </p>
          <div className={styles.miniActions}>
            <button onClick={handleReject} className={styles.miniRejectBtn}>
              {isIt ? 'Rifiuta' : 'Reject'}
            </button>
            <button onClick={handleAccept} className={styles.miniAcceptBtn}>
              {isIt ? 'Accetta' : 'Accept'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ───────── FULL MODAL for all other pages ───────── */
  return (
    <>
      <div className={`${styles.overlay} ${animatingOut ? styles.overlayExit : styles.overlayEnter}`} />
      <div
        className={`${styles.modal} ${animatingOut ? (accepted ? styles.modalExitAccepted : styles.modalExit) : styles.modalEnter}`}
        role="dialog"
        aria-label={isIt ? 'Consenso cookie' : 'Cookie consent'}
      >
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>
            {isIt ? 'Informazioni sui cookie' : 'Cookie information'}
          </h2>
          <p className={styles.subtitle}>
            {isIt
              ? 'Clicca su una stazione per scoprire di più. Leggi la '
              : 'Click a station to learn more. Read the '}
            <Link to="/cookies" className={styles.policyLink}>Cookie Policy</Link>
            {isIt ? ' o la ' : ' or the '}
            <Link to="/privacy" className={styles.policyLink}>Privacy Policy</Link>.
          </p>
        </div>

        {/* Metro Map Track */}
        <div className={styles.metroMap}>
          <svg viewBox="0 0 480 60" className={styles.trackSvg} preserveAspectRatio="xMidYMid meet">
            {/* Main track line */}
            <line x1="30" y1="30" x2="450" y2="30" className={styles.rail} />

            {/* Cross-ties */}
            {Array.from({ length: 22 }).map((_, i) => (
              <line
                key={`tie-${i}`}
                x1={40 + i * 19}
                y1="24"
                x2={40 + i * 19}
                y2="36"
                className={styles.tie}
              />
            ))}

            {/* Animated train dot */}
            <g className={`${styles.trainDot} ${animatingOut && accepted ? styles.trainDotDepart : ''}`}>
              <circle cx="0" cy="0" r="6" className={styles.trainPulse} />
              <circle cx="0" cy="0" r="3.5" className={styles.trainCore} />
            </g>

            {/* Station nodes */}
            {stations.map((station, i) => {
              const pos = STATION_POSITIONS[i];
              const isActive = activeStation === station.id;
              return (
                <g key={station.id}>
                  {/* Station click target (larger invisible area) */}
                  <circle
                    cx={pos.cx}
                    cy={pos.cy}
                    r="18"
                    fill="transparent"
                    cursor="pointer"
                    onClick={() => setActiveStation(isActive ? null : station.id)}
                  />
                  {/* Station outer ring */}
                  <circle
                    cx={pos.cx}
                    cy={pos.cy}
                    r="10"
                    className={`${styles.stationOuter} ${isActive ? styles.stationOuterActive : ''}`}
                  />
                  {/* Station inner dot */}
                  <circle
                    cx={pos.cx}
                    cy={pos.cy}
                    r="5"
                    className={`${styles.stationInner} ${isActive ? styles.stationInnerActive : ''}`}
                  />
                </g>
              );
            })}
          </svg>

          {/* Station labels below track */}
          <div className={styles.stationLabels}>
            {stations.map((station, i) => {
              const isActive = activeStation === station.id;
              return (
                <button
                  key={station.id}
                  className={`${styles.stationLabel} ${isActive ? styles.stationLabelActive : ''}`}
                  onClick={() => setActiveStation(isActive ? null : station.id)}
                  style={{ left: `${(STATION_POSITIONS[i].cx / 480) * 100}%` }}
                >
                  {station.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Expanded station detail */}
        {activeStation && (
          <div className={styles.stationDetail} key={activeStation}>
            <div className={styles.detailBadge}>
              {stations.find(s => s.id === activeStation)?.label}
            </div>
            <p className={styles.detailText}>
              {stations.find(s => s.id === activeStation)?.detail}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className={styles.actions}>
          <button onClick={handleAccept} className={styles.acceptBtn}>
            {isIt ? 'Consenti tutti i cookie' : 'Allow all cookies'}
          </button>
          <button onClick={handleReject} className={styles.rejectBtn}>
            {isIt ? 'Rifiuta cookie facoltativi' : 'Reject optional cookies'}
          </button>
        </div>
      </div>
    </>
  );
};
